import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Button, Card, DatePicker, Input, InputNumber, Segmented, Tag,
} from 'antd';
import {
  AppstoreOutlined, BarsOutlined, CheckOutlined, CloseOutlined, DownloadOutlined, EditOutlined,
  FileSearchOutlined, SearchOutlined, SwapOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import './orderXV4Scan.css';
import EditMatchModal from './EditMatchModal';
import EditItemDetailsModal from './EditItemDetailsModal';
import MatchSourceBadge from './MatchSourceBadge';
import CustomerShipToContactCards from './CustomerShipToContactCards';
import DocumentPreviewPanel from './DocumentPreviewPanel';
import RequestSourcePreviewModal from './RequestSourcePreviewModal';
import {
  CARD_STYLES, ExtractedFieldsBlock, ProductImage, PricingPanel, SimpleExtractedFields,
} from './LineItemPriceDisplay';
import {
  buildProductImageUrl,
  computeReviewTotals,
  decodeHtmlEntities,
  exportReviewToExcel,
  formatUnitPrice,
  getErpMatchBadge,
  getMatchAiBadge,
  getPriceDivergenceBadge,
  getUomMismatchBadge,
  groupDuplicateItems,
  mergeDuplicateGroup,
  resolveEffectiveLinePrice,
  resolveFieldState,
} from '../../pages/orderXV4Utils';

const { TextArea } = Input;

// Displays the customer name/addresses AS WRITTEN ON THE SOURCE DOCUMENT itself
// — distinct from (and shown alongside) the ERP customer/ship-to the user picks
// in CustomerShipToContactCards/StepCustomerShipping. Unlike every other
// extracted field (which already has an edit path: line-item fields via
// EditItemDetailsModal, header fields via inline inputs), these four had no
// edit affordance at all until now.
//
// Each field is plain read-only text until its own explicit pencil icon is
// clicked — never an always-on inline input — so a stray keystroke while
// scrolling/reading the page can never mutate a value. Clicking it swaps just
// that one row into an edit control with explicit Save/Cancel icons; every
// other row stays locked. Only one field edits at a time (`editingKey`).
const ExtractedDocumentInfoCard = ({ extractedInfo, onFieldChange }) => {
  const [editingKey, setEditingKey] = useState(null);
  const [draftValue, setDraftValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const rows = [
    { key: 'customer_name', label: 'Customer Name' },
    { key: 'customer_address', label: 'Customer Address', multiline: true },
    { key: 'ship_to_address', label: 'Ship To Address', multiline: true },
    { key: 'bill_to_address', label: 'Bill To Address', multiline: true },
  ];

  const startEditing = (row) => {
    setEditingKey(row.key);
    setDraftValue(extractedInfo?.[row.key] || '');
    setError('');
  };

  const cancelEditing = () => {
    if (saving) return;
    setEditingKey(null);
    setError('');
  };

  const saveEditing = async (row) => {
    setSaving(true);
    setError('');
    try {
      await onFieldChange?.(row.key, draftValue.trim());
      setEditingKey(null);
    } catch (err) {
      setError(err?.message || 'Unable to save.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="shadow-sm border-0 mb-3">
    <div className="text-muted small fw-semibold mb-2" style={{ letterSpacing: 0.3 }}>
        AS EXTRACTED FROM DOCUMENT
      </div>
      <div className="row g-2">
        {rows.map((row) => {
          const isEditing = editingKey === row.key;
          const value = extractedInfo?.[row.key] || '';
          return (
            <div className="col-12 col-md-6" key={row.key}>
              <div className="d-flex align-items-center justify-content-between">
                <div className="text-muted small">{row.label}</div>
                {!isEditing && (
                  <Button
                    type="text"
                    size="small"
                    icon={<EditOutlined />}
                    title={`Edit ${row.label}`}
                    aria-label={`Edit ${row.label}`}
                    onClick={() => startEditing(row)}
                  />
                )}
    </div>
              {isEditing ? (
                <div>
                  {row.multiline ? (
                    <TextArea
                      autoFocus
                      rows={2}
                      value={draftValue}
                      onChange={(e) => setDraftValue(e.target.value)}
                      disabled={saving}
                    />
                  ) : (
                    <Input
                      autoFocus
                      value={draftValue}
                      onChange={(e) => setDraftValue(e.target.value)}
                      disabled={saving}
                      onPressEnter={() => saveEditing(row)}
                    />
                  )}
                  <div className="d-flex gap-1 mt-1">
                    <Button
                      size="small"
                      type="primary"
                      icon={<CheckOutlined />}
                      loading={saving}
                      onClick={() => saveEditing(row)}
                    >
                      Save
                    </Button>
                    <Button size="small" icon={<CloseOutlined />} disabled={saving} onClick={cancelEditing}>
                      Cancel
                    </Button>
                  </div>
                  {error && <div className="text-danger small mt-1">{error}</div>}
    </div>
              ) : (
                <div className="small" style={{ whiteSpace: 'pre-wrap' }}>{value || <span className="text-muted">—</span>}</div>
              )}
  </div>
);
        })}
      </div>
    </Card>
  );
};

const FieldInput = ({ field, value, onChange }) => {
  if (field.type === 'date') {
    return (
      <DatePicker
        style={{ width: '100%' }}
        value={value ? dayjs(value) : null}
        onChange={(d) => onChange(d ? d.format('YYYY-MM-DD') : '')}
      />
    );
  }
  if (field.type === 'textarea') {
    return <TextArea rows={2} value={value || ''} onChange={(e) => onChange(e.target.value)} />;
  }
  return <Input value={value || ''} onChange={(e) => onChange(e.target.value)} />;
};

// Shown above the item list for each group of lines sharing a backend-tagged
// duplicate_group_key. Offers Merge (preview extracted+matched info per source
// line, then the resulting summed-qty line, before confirming) or Keep Separate
// (records the choice so the group moves to the Resolved tab instead of
// re-prompting every render — see spec-orderxv4-duplicate-line-merge). `mode` is
// controlled by the parent (rather than local state) so "Expand all" can open
// every group's preview at once.
const DuplicateGroupNote = ({
  group, lineNumberByKey, matchesByItemKey, selectedByItem, qtyByItem, onMerge, onKeepSeparate,
  checked, onToggleChecked, mode, onModeChange,
}) => {
  const previewing = mode || null; // null | 'expanded' | 'merge' | 'separate'
  const setPreviewing = (next) => onModeChange(group.key, next);
  const memberIds = group.items.map((item) => String(item.id || ''));
  const lineLabel = memberIds.map((key) => `LINE ${lineNumberByKey[key]}`).join(', ');

  const memberRows = group.items.map((item) => {
    const key = String(item.id || '');
    const itemMatches = matchesByItemKey[key] || [];
    const selectedCode = selectedByItem[key] || itemMatches[0]?.product_code;
    const selectedMatch = itemMatches.find((m) => m.product_code === selectedCode) || itemMatches[0] || null;
    const qty = Number(qtyByItem[key] ?? item?.normalized_parts?.qty ?? 1) || 1;
    return (
      <div className="small border rounded p-2 bg-white" key={key}>
        <span className="fw-semibold">LINE {lineNumberByKey[key]}:</span>{' '}
        {decodeHtmlEntities(item?.normalized_parts?.part_number || '') || '—'} —{' '}
        {decodeHtmlEntities(item?.normalized_parts?.description || '') || '—'} — qty {qty}
        {selectedMatch ? ` — matched ${selectedMatch.product_code}` : ' — no match'}
      </div>
    );
  });

  if (previewing === 'merge') {
    const merged = mergeDuplicateGroup(group.items, qtyByItem);
    return (
      <div className="alert alert-warning py-3 mb-3">
        <div className="fw-semibold mb-2">Merge preview — {lineLabel}</div>
        <div className="d-flex flex-column gap-1 mb-2">{memberRows}</div>
        <div className="small border rounded p-2 mb-2" style={{ background: '#fffbe6', borderColor: '#faad14' }}>
          <span className="fw-semibold">Merged line:</span>{' '}
          {decodeHtmlEntities(merged.part_number) || decodeHtmlEntities(merged.description) || '—'} — qty {merged.qty}
        </div>
        <div className="d-flex gap-2">
          <Button size="small" type="primary" onClick={() => onMerge(group.key, merged, memberIds, lineLabel)}>
            Confirm merge
          </Button>
          <Button size="small" onClick={() => setPreviewing(null)}>Cancel</Button>
        </div>
      </div>
    );
  }

  if (previewing === 'separate') {
    return (
      <div className="alert alert-secondary py-3 mb-3">
        <div className="fw-semibold mb-2">Keep separate — {lineLabel}</div>
        <div className="d-flex flex-column gap-1 mb-2">{memberRows}</div>
        <div className="small mb-2">These lines will be left as individual lines instead of merged.</div>
        <div className="d-flex gap-2">
          <Button size="small" type="primary" onClick={() => onKeepSeparate(group.key, lineLabel)}>
            Confirm keep separate
          </Button>
          <Button size="small" onClick={() => setPreviewing(null)}>Cancel</Button>
        </div>
      </div>
    );
  }

  if (previewing === 'expanded') {
    return (
      <div className="alert alert-info py-3 mb-3">
        <div className="fw-semibold mb-2">{lineLabel} look like duplicate lines (same part/description).</div>
        <div className="d-flex flex-column gap-1 mb-2">{memberRows}</div>
        <div className="d-flex gap-2">
          <Button size="small" onClick={() => setPreviewing('merge')}>Merge</Button>
          <Button size="small" onClick={() => setPreviewing('separate')}>Keep separate</Button>
          <Button size="small" onClick={() => setPreviewing(null)}>Cancel</Button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="alert alert-info py-2 mb-3 d-flex justify-content-between align-items-center flex-wrap gap-2"
      role="button"
      tabIndex={0}
      style={{ cursor: 'pointer' }}
      onClick={() => setPreviewing('expanded')}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          setPreviewing('expanded');
        }
      }}
    >
      <span className="small d-flex align-items-center gap-2">
        <input
          type="checkbox"
          checked={!!checked}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => { e.stopPropagation(); onToggleChecked(group.key); }}
          aria-label={`Select duplicate group ${lineLabel}`}
        />
        {lineLabel} look like duplicate lines (same part/description).
      </span>
      <div className="d-flex gap-2">
        <Button size="small" onClick={(e) => { e.stopPropagation(); setPreviewing('merge'); }}>Merge</Button>
        <Button size="small" onClick={(e) => { e.stopPropagation(); setPreviewing('separate'); }}>Keep separate</Button>
      </div>
    </div>
  );
};

const StepReviewMatch = ({
  items,
  matches,
  selectedByItem,
  onSelectMatch,
  qtyByItem,
  onQtyChange,
  erpPriceByProductCode,
  erpPriceLoadingByProductCode,
  erpUomByProductCode = {},
  loadErpPricesForCodes,
  headerFields,
  onHeaderFieldChange,
  checkoutFlowConfig,
  formValues,
  onContinue,
  productImageBaseUrl,
  onAddManualMatch,
  pricingEngine,
  resolvedDuplicateGroups,
  onMergeDuplicateGroup,
  onKeepDuplicatesSeparate,
  onBulkMergeDuplicateGroups,
  onBulkKeepDuplicatesSeparate,
  duplicateResolutionLog,
  onEditItemFields,
  requestId,
  userId,
  extractedInfo,
  onExtractedInfoFieldChange,
}) => {
  const [editModalItemKey, setEditModalItemKey] = useState(null);
  const [showDocPanel, setShowDocPanel] = useState(true);
  // 'detailed' (Card per line, with product image) vs 'simple' (dense table,
  // no product image, just the extracted/matched data fields) -- simple trades
  // the image for screen space so more line items are visible without scrolling.
  const [viewMode, setViewMode] = useState('detailed');
  const [expandedDocPreviewOpen, setExpandedDocPreviewOpen] = useState(false);
  // Separate from editModalItemKey (which drives EditMatchModal, match-selection
  // only) — this drives EditItemDetailsModal, full extracted-field editing.
  const [editFieldsItemKey, setEditFieldsItemKey] = useState(null);
  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);
  const [selectedGroupKeys, setSelectedGroupKeys] = useState([]);
  const [bulkAction, setBulkAction] = useState(null); // null | { mode: 'merge' | 'separate', groupKeys: string[] }
  const [groupModeByKey, setGroupModeByKey] = useState({}); // groupKey -> null | 'expanded' | 'merge' | 'separate'
  const [dupTab, setDupTab] = useState('pending'); // 'pending' | 'resolved'
  // 'all' | 'exact' | 'ai_reranked' | 'no_matches' -- mirrors the OrderX v3 match
  // filter so reviewers can isolate lines that need attention without scrolling.
  const [reviewMatchFilter, setReviewMatchFilter] = useState('all');

  const duplicateGroups = useMemo(() => groupDuplicateItems(items), [items]);
  const unresolvedDuplicateGroups = useMemo(
    () => duplicateGroups.filter((g) => resolvedDuplicateGroups?.[g.key] !== 'separate'),
    [duplicateGroups, resolvedDuplicateGroups]
  );
  const resolvedLog = duplicateResolutionLog || [];

  const lineNumberByKey = useMemo(() => {
    const map = {};
    (items || []).forEach((item, idx) => { map[String(item.id || '')] = idx + 1; });
    return map;
  }, [items]);

  const toggleGroupChecked = (groupKey) => {
    setSelectedGroupKeys((prev) => (prev.includes(groupKey)
      ? prev.filter((k) => k !== groupKey)
      : [...prev, groupKey]));
  };

  const setGroupMode = (groupKey, mode) => {
    setGroupModeByKey((prev) => ({ ...prev, [groupKey]: mode }));
  };

  const expandAllGroups = () => {
    setGroupModeByKey(Object.fromEntries(unresolvedDuplicateGroups.map((g) => [g.key, 'expanded'])));
  };

  const collapseAllGroups = () => setGroupModeByKey({});

  const lineLabelForGroup = (group) => group.items
    .map((item) => `LINE ${lineNumberByKey[String(item.id || '')]}`)
    .join(', ');

  const runBulkAction = () => {
    if (!bulkAction) return;
    if (bulkAction.mode === 'merge') {
      const entries = bulkAction.groupKeys
        .map((groupKey) => duplicateGroups.find((g) => g.key === groupKey))
        .filter(Boolean)
        .map((group) => ({
          groupKey: group.key,
          mergedPreview: mergeDuplicateGroup(group.items, qtyByItem),
          memberIds: group.items.map((item) => String(item.id || '')),
          lineLabel: lineLabelForGroup(group),
        }));
      onBulkMergeDuplicateGroups(entries);
    } else {
      const groupsByKey = Object.fromEntries(duplicateGroups.map((g) => [g.key, g]));
      const lineLabelByKey = Object.fromEntries(
        bulkAction.groupKeys
          .filter((k) => groupsByKey[k])
          .map((k) => [k, lineLabelForGroup(groupsByKey[k])])
      );
      onBulkKeepDuplicatesSeparate(bulkAction.groupKeys, lineLabelByKey);
    }
    setSelectedGroupKeys((prev) => prev.filter((k) => !bulkAction.groupKeys.includes(k)));
    setGroupModeByKey((prev) => {
      const next = { ...prev };
      bulkAction.groupKeys.forEach((k) => { delete next[k]; });
      return next;
    });
    setBulkAction(null);
  };

  const { subtotal, unmatchedCount, matchesByItemKey } = useMemo(
    () => computeReviewTotals(items, matches, selectedByItem, qtyByItem, erpPriceByProductCode, pricingEngine),
    [items, matches, selectedByItem, qtyByItem, erpPriceByProductCode, pricingEngine]
  );

  const orderType = formValues.order_type || 'quote';
  const blockedByUnmatched = orderType === 'order' && unmatchedCount > 0;

  // Classifies a line by its match options' backend-stamped `ai_ranked` flag
  // (see order_genie_service match builders) -- same signal OrderX v3 uses to
  // badge AI Re-ranked lines (orderXV4Utils.getMatchAiBadge).
  const categoryForItemKey = (key) => {
    const itemMatches = matchesByItemKey[key] || [];
    if (itemMatches.length === 0) return 'no_matches';
    if (itemMatches.some((m) => m?.ai_ranked)) return 'ai_reranked';
    return 'exact';
  };

  const reviewFilterCounts = useMemo(() => {
    const counts = { all: (items || []).length, exact: 0, ai_reranked: 0, no_matches: 0 };
    (items || []).forEach((item) => {
      const key = String(item?.id || '');
      if (!key) return;
      counts[categoryForItemKey(key)] += 1;
    });
    return counts;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, matchesByItemKey]);

  const filteredItems = useMemo(() => {
    if (reviewMatchFilter === 'all') return items || [];
    return (items || []).filter((item) => categoryForItemKey(String(item?.id || '')) === reviewMatchFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, matchesByItemKey, reviewMatchFilter]);

  // Prefetch price for the top/selected match per line, lazily — never the whole candidate list.
  useEffect(() => {
    const codes = (items || []).map((item) => {
      const key = String(item.id || '');
      const itemMatches = matchesByItemKey[key] || [];
      return selectedByItem[key] || itemMatches[0]?.product_code;
    }).filter(Boolean);
    loadErpPricesForCodes(codes);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, matchesByItemKey]);

  const reviewFields = checkoutFlowConfig?.steps?.review_match?.fields || [];

  const editModalItem = (items || []).find((item) => String(item.id || '') === editModalItemKey) || null;
  const editModalMatches = editModalItemKey ? (matchesByItemKey[editModalItemKey] || []) : [];
  const editModalSelectedCode = editModalItemKey
    ? (selectedByItem[editModalItemKey] || editModalMatches[0]?.product_code)
    : null;

  const editFieldsItem = (items || []).find((item) => String(item.id || '') === editFieldsItemKey) || null;

  // Shared per-line-item derivation used by both the Detailed (Card) and Simple
  // (dense table) renderers below, so the two views can never drift out of sync
  // on match/price resolution -- only their JSX shape differs.
  const computeLineItemView = (item, idx) => {
    const key = String(item.id || '');
    const itemMatches = matchesByItemKey[key] || [];
    const selectedCode = selectedByItem[key] || itemMatches[0]?.product_code;
    const selectedMatch = itemMatches.find((m) => m.product_code === selectedCode) || itemMatches[0] || null;
    const qty = Number(qtyByItem[key] ?? item?.normalized_parts?.qty ?? 1) || 1;
    const inputUnitPrice = Number(item?.normalized_parts?.input_unit_price);
    const inputExtendedPrice = Number(item?.normalized_parts?.input_extended_price);
    const { unitPrice, extendedPrice, usingInputPriceFallback, uom } = resolveEffectiveLinePrice({
      matchUnitPrice: selectedMatch?.pricing?.unit_price,
      erpUnitPrice: erpPriceByProductCode[selectedCode],
      inputUnitPrice,
      inputExtendedPrice,
      pricingEngine,
      qty,
      matchUom: selectedMatch?.pricing?.uom,
      erpUom: erpUomByProductCode?.[selectedCode],
      fallbackUom: item?.normalized_parts?.uom,
    });
    const hasNoMatch = itemMatches.length === 0;
    const imageSrc = buildProductImageUrl(selectedMatch?.match?.image_url, productImageBaseUrl);
    return {
      key, idx, item, itemMatches, selectedCode, selectedMatch, qty, uom,
      inputUnitPrice, inputExtendedPrice, unitPrice, extendedPrice,
      usingInputPriceFallback, hasNoMatch, imageSrc,
    };
  };

  return (
    <div>
      <div
        className="orderx-review-sticky-bar bg-white pb-3"
        style={{ position: 'sticky', top: 0, zIndex: 20 }}
      >
        <div className="d-flex flex-wrap align-items-center gap-4 p-3 bg-white border rounded-3 shadow-sm">
          <div className="d-flex align-items-center gap-4 flex-wrap">
            <div>
              <div className="text-muted small">Line items</div>
              <div className="fw-semibold">{(items || []).length}</div>
            </div>
            {unmatchedCount > 0 && (
              <div>
                <div className="text-muted small">Unmatched</div>
                <div className="fw-semibold text-danger">{unmatchedCount}</div>
              </div>
            )}
            <div>
              <div className="text-muted small">Subtotal</div>
              <div className="fw-semibold fs-5">${subtotal.toFixed(2)}</div>
            </div>
          </div>

          <div className="d-flex align-items-center gap-2 ms-lg-auto flex-wrap">
            <Button type="primary" size="large" onClick={onContinue} disabled={blockedByUnmatched}>
              Continue
            </Button>
          </div>

          <div className="d-flex align-items-center gap-2 flex-wrap w-100">
            <span className="text-muted small">Filter:</span>
            <Button
              size="small"
              type={reviewMatchFilter === 'all' ? 'primary' : 'default'}
              onClick={() => setReviewMatchFilter('all')}
            >
              {`All (${reviewFilterCounts.all})`}
            </Button>
            <Button
              size="small"
              type={reviewMatchFilter === 'exact' ? 'primary' : 'default'}
              onClick={() => setReviewMatchFilter('exact')}
            >
              {`Exact matches (${reviewFilterCounts.exact})`}
            </Button>
            <Button
              size="small"
              type={reviewMatchFilter === 'ai_reranked' ? 'primary' : 'default'}
              onClick={() => setReviewMatchFilter('ai_reranked')}
            >
              {`AI Re-ranked (${reviewFilterCounts.ai_reranked})`}
            </Button>
            <Button
              size="small"
              danger={reviewMatchFilter === 'no_matches'}
              type={reviewMatchFilter === 'no_matches' ? 'primary' : 'default'}
              onClick={() => setReviewMatchFilter('no_matches')}
            >
              {`No matches (${reviewFilterCounts.no_matches})`}
            </Button>
          </div>

          {blockedByUnmatched && (
            <div className="alert alert-warning py-1 px-2 mb-0 small w-100">
              {unmatchedCount} line{unmatchedCount === 1 ? '' : 's'} unmatched — resolve the match above or switch to Quote.
            </div>
          )}
        </div>
      </div>

    <div className="row g-4">
        {showDocPanel && requestId != null && (
          <div className="col-12 col-lg-6">
            <div style={{ position: 'sticky', top: 80, maxHeight: '82vh', overflow: 'auto' }}>
              <DocumentPreviewPanel
                requestId={requestId}
                userId={userId}
                onExpand={() => setExpandedDocPreviewOpen(true)}
              />
            </div>
          </div>
        )}

        <RequestSourcePreviewModal
          requestId={requestId}
          userId={userId}
          open={expandedDocPreviewOpen}
          onClose={() => setExpandedDocPreviewOpen(false)}
        />

        <div className={showDocPanel && requestId != null ? 'col-12 col-lg-6' : 'col-12'}>
        <ExtractedDocumentInfoCard extractedInfo={extractedInfo} onFieldChange={onExtractedInfoFieldChange} />
        <CustomerShipToContactCards formValues={formValues} className="mb-3" />
        <Card className="shadow-sm border-0 mb-3">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="mb-0">Request Details</h5>
            <div className="d-flex gap-2">
              {requestId != null && (
                <Button
                  size="small"
                  icon={<FileSearchOutlined />}
                  onClick={() => setShowDocPanel((v) => !v)}
                >
                  {showDocPanel ? 'Hide input document' : 'Show input document'}
                </Button>
              )}
            <Button
              size="small"
              icon={<DownloadOutlined />}
              onClick={() => exportReviewToExcel({
                items, matches, selectedByItem, qtyByItem, erpPriceByProductCode, pricingEngine,
                fileNamePrefix: 'orderx_review_match',
              })}
            >
              Export to Excel
            </Button>
            </div>
          </div>
          <div className="row g-3">
            {reviewFields.map((field) => {
              const { visible } = resolveFieldState(field, formValues);
              if (!visible) return null;
              return (
                <div className="col-12 col-md-6" key={field.key}>
                  <label className="form-label small">{field.label}</label>
                  <FieldInput
                    field={field}
                    value={headerFields[field.key]}
                    onChange={(val) => onHeaderFieldChange(field.key, val)}
                  />
                </div>
              );
            })}
          </div>
        </Card>

        {(unresolvedDuplicateGroups.length > 0 || resolvedLog.length > 0) && (
          <div className="d-flex gap-2 mb-2">
            <Button
              size="small"
              type={dupTab === 'pending' ? 'primary' : 'default'}
              onClick={() => setDupTab('pending')}
            >
              Needs review ({unresolvedDuplicateGroups.length})
            </Button>
            <Button
              size="small"
              type={dupTab === 'resolved' ? 'primary' : 'default'}
              onClick={() => setDupTab('resolved')}
            >
              Resolved ({resolvedLog.length})
            </Button>
          </div>
        )}

        {dupTab === 'pending' && unresolvedDuplicateGroups.length > 0 && !bulkAction && (
          <div className="d-flex align-items-center flex-wrap gap-2 mb-2 p-2 border rounded bg-light">
            <label className="d-flex align-items-center gap-1 small mb-0">
              <input
                type="checkbox"
                checked={selectedGroupKeys.length > 0 && selectedGroupKeys.length === unresolvedDuplicateGroups.length}
                onChange={(e) => setSelectedGroupKeys(e.target.checked ? unresolvedDuplicateGroups.map((g) => g.key) : [])}
              />
              Select all duplicate groups ({unresolvedDuplicateGroups.length})
            </label>
            <Button size="small" onClick={expandAllGroups}>Expand all</Button>
            <Button size="small" onClick={collapseAllGroups}>Collapse all</Button>
            <Button
              size="small"
              disabled={selectedGroupKeys.length === 0}
              onClick={() => setBulkAction({ mode: 'merge', groupKeys: selectedGroupKeys })}
            >
              Merge selected ({selectedGroupKeys.length})
            </Button>
            <Button
              size="small"
              disabled={selectedGroupKeys.length === 0}
              onClick={() => setBulkAction({ mode: 'separate', groupKeys: selectedGroupKeys })}
            >
              Keep selected separate ({selectedGroupKeys.length})
            </Button>
            <Button
              size="small"
              onClick={() => setBulkAction({ mode: 'merge', groupKeys: unresolvedDuplicateGroups.map((g) => g.key) })}
            >
              Merge all groups
            </Button>
            <Button
              size="small"
              onClick={() => setBulkAction({ mode: 'separate', groupKeys: unresolvedDuplicateGroups.map((g) => g.key) })}
            >
              Keep all groups separate
            </Button>
          </div>
        )}

        {dupTab === 'pending' && bulkAction && (
          <div className={`alert ${bulkAction.mode === 'merge' ? 'alert-warning' : 'alert-secondary'} py-3 mb-3`}>
            <div className="fw-semibold mb-2">
              {bulkAction.mode === 'merge' ? 'Bulk merge preview' : 'Bulk keep separate'} — {bulkAction.groupKeys.length} group{bulkAction.groupKeys.length !== 1 ? 's' : ''}
            </div>
            <div className="d-flex flex-column gap-2 mb-2">
              {bulkAction.groupKeys.map((groupKey) => {
                const group = duplicateGroups.find((g) => g.key === groupKey);
                if (!group) return null;
                const lineLabel = lineLabelForGroup(group);
                const merged = bulkAction.mode === 'merge' ? mergeDuplicateGroup(group.items, qtyByItem) : null;
                return (
                  <div className="border rounded p-2 bg-white" key={groupKey}>
                    <div className="small fw-semibold mb-1">{lineLabel}</div>
                    {group.items.map((item) => {
                      const key = String(item.id || '');
                      const itemMatches = matchesByItemKey[key] || [];
                      const selectedCode = selectedByItem[key] || itemMatches[0]?.product_code;
                      const selectedMatch = itemMatches.find((m) => m.product_code === selectedCode) || itemMatches[0] || null;
                      const qty = Number(qtyByItem[key] ?? item?.normalized_parts?.qty ?? 1) || 1;
                      return (
                        <div className="small ps-2" key={key}>
                          LINE {lineNumberByKey[key]}: {decodeHtmlEntities(item?.normalized_parts?.part_number || '') || '—'} —{' '}
                          {decodeHtmlEntities(item?.normalized_parts?.description || '') || '—'} — qty {qty}
                          {selectedMatch ? ` — matched ${selectedMatch.product_code}` : ' — no match'}
                        </div>
                      );
                    })}
                    {merged && (
                      <div className="small mt-1" style={{ color: '#ad6800' }}>
                        Merged line: {decodeHtmlEntities(merged.part_number) || decodeHtmlEntities(merged.description) || '—'} — qty {merged.qty}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="d-flex gap-2">
              <Button size="small" type="primary" onClick={runBulkAction}>
                {bulkAction.mode === 'merge' ? 'Confirm merge all' : 'Confirm keep separate'}
              </Button>
              <Button size="small" onClick={() => setBulkAction(null)}>Cancel</Button>
            </div>
          </div>
        )}

        {dupTab === 'pending' && !bulkAction && unresolvedDuplicateGroups.map((group) => (
          <DuplicateGroupNote
            key={group.key}
            group={group}
            lineNumberByKey={lineNumberByKey}
            matchesByItemKey={matchesByItemKey}
            selectedByItem={selectedByItem}
            qtyByItem={qtyByItem}
            onMerge={onMergeDuplicateGroup}
            onKeepSeparate={onKeepDuplicatesSeparate}
            checked={selectedGroupKeys.includes(group.key)}
            onToggleChecked={toggleGroupChecked}
            mode={groupModeByKey[group.key]}
            onModeChange={setGroupMode}
          />
        ))}

        {dupTab === 'resolved' && (
          resolvedLog.length === 0 ? (
            <div className="text-muted small mb-3">No duplicate groups resolved yet.</div>
          ) : (
            <div className="d-flex flex-column gap-2 mb-3">
              {resolvedLog.map((entry) => (
                <div className="alert alert-light border py-2 mb-0 small" key={entry.key}>
                  <span className={`badge ${entry.mode === 'merged' ? 'bg-warning text-dark' : 'bg-secondary'} me-2`}>
                    {entry.mode === 'merged' ? 'Merged' : 'Kept separate'}
                  </span>
                  {entry.lineLabel}
                  {entry.detail ? ` — ${entry.detail}` : ''}
                  {(entry.actorUserId || entry.createdAt) && (
                    <div className="text-muted">
                      {entry.actorUserId ? `by ${entry.actorUserId}` : ''}
                      {entry.actorUserId && entry.createdAt ? ' on ' : ''}
                      {entry.createdAt ? dayjs(entry.createdAt).format('MMM D, YYYY h:mm A') : ''}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )
        )}

        <div className="d-flex justify-content-between align-items-center mb-2">
          <div className="text-muted small">
            {reviewMatchFilter === 'all'
              ? `${(items || []).length} line item${(items || []).length === 1 ? '' : 's'}`
              : `${filteredItems.length} of ${(items || []).length} line items`}
              </div>
          <Segmented
            size="small"
            value={viewMode}
            onChange={setViewMode}
            aria-label="Line items view"
            options={[
              { value: 'detailed', icon: <AppstoreOutlined />, label: 'Detailed' },
              { value: 'simple', icon: <BarsOutlined />, label: 'Simple' },
            ]}
          />
        </div>

        {viewMode === 'simple' ? (
          <div className="table-responsive">
            <table className="table table-sm table-bordered align-middle mb-0 bg-white" style={{ tableLayout: 'fixed' }}>
              <colgroup>
                <col style={{ width: 36 }} />
                <col style={{ width: '34%' }} />
                <col style={{ width: '34%' }} />
                <col style={{ width: 90 }} />
                <col style={{ width: 110 }} />
                <col style={{ width: 36 }} />
              </colgroup>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Extracted from Document</th>
                  <th>Matched Product</th>
                  <th className="text-end">Qty</th>
                  <th className="text-end">Price</th>
                  <th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {filteredItems.length === 0 && (
                  <tr>
                    <td colSpan={6} className="text-muted small text-center py-3">
                      No line items match this filter.
                    </td>
                  </tr>
                )}
                {filteredItems.map((item, idx) => {
                  const v = computeLineItemView(item, idx);
                  return (
                    <tr key={v.key} className={v.hasNoMatch ? 'table-danger' : undefined}>
                      <td className="text-muted small">{lineNumberByKey[v.key]}</td>
                      <td>
                        <SimpleExtractedFields
                          partNumber={item?.normalized_parts?.part_number || ''}
                          manufacturer={item?.normalized_parts?.manufacturer || ''}
                          manufacturerPartNumber={item?.normalized_parts?.manufacturer_part_number || ''}
                          customerPartNumber={item?.normalized_parts?.customer_part_number || ''}
                          description={item?.normalized_parts?.description || ''}
                          notes={item?.normalized_parts?.notes || ''}
                        />
                      </td>
                      <td>
                        {v.selectedMatch ? (
                          <>
                            <div className="fw-semibold small">
                              {v.selectedMatch.product_code}
                              {v.itemMatches.length > 1 ? ` (${v.itemMatches.length} options)` : ''}
                            </div>
                            <div
                              className="text-muted small"
                              title={decodeHtmlEntities(v.selectedMatch.match?.name || '')}
                              style={{
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                              }}
                            >
                              {decodeHtmlEntities(v.selectedMatch.match?.name || '')}
                            </div>
                          </>
                        ) : (
                          <Tag color="red">No match</Tag>
                        )}
                        <div className="mt-1">
                          <Button
                            size="small"
                            danger={v.hasNoMatch}
                            icon={v.hasNoMatch ? <SearchOutlined /> : <SwapOutlined />}
                            onClick={() => setEditModalItemKey(v.key)}
                          >
                            {v.hasNoMatch ? 'Search' : 'Edit match'}
                          </Button>
                        </div>
                      </td>
                      <td className="text-end">
                        <InputNumber
                          size="small"
                          min={1}
                          precision={0}
                          value={v.qty}
                          onChange={(val) => onQtyChange(v.key, val || 1)}
                          style={{ width: 70 }}
                        />
                      </td>
                      <td className="text-end small">
                        {/* Same From Doc block, then ERP block ordering (unit price
                            before extended, within each) as PricingPanel's Detailed
                            view -- see LineItemPriceDisplay.jsx's PricingPanel. */}
                        {Number.isFinite(v.inputUnitPrice) && v.inputUnitPrice > 0 && (
                          <>
                            <div className="text-muted" style={{ fontSize: 11 }}>From Doc</div>
                            <div>{`$${v.inputUnitPrice.toFixed(2)}/ea`}</div>
                            <div className="text-muted mb-1" style={{ fontSize: 11 }}>
                              {`Ext: ${Number.isFinite(v.inputExtendedPrice) && v.inputExtendedPrice > 0
                                ? `$${v.inputExtendedPrice.toFixed(2)}`
                                : '—'}`}
                            </div>
                          </>
                        )}
                        <div className="text-muted" style={{ fontSize: 11 }}>ERP</div>
                        <div>
                          {formatUnitPrice(v.unitPrice, v.uom)}
                        </div>
                        <div className="fw-semibold" style={{ color: '#0f6b3f' }}>
                          {`Ext: ${Number.isFinite(v.extendedPrice) ? `$${v.extendedPrice.toFixed(2)}` : '—'}`}
                        </div>
                      </td>
                      <td>
                        <Button
                          size="small"
                          type="text"
                          icon={<EditOutlined />}
                          title="Edit Details"
                          aria-label="Edit Details"
                          onClick={() => setEditFieldsItemKey(v.key)}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : filteredItems.length === 0 ? (
          <div className="text-muted small text-center py-4 bg-white border rounded-3">
            No line items match this filter.
          </div>
        ) : (
          filteredItems.map((item, idx) => {
            const v = computeLineItemView(item, idx);
            const {
              key, itemMatches, selectedCode, selectedMatch, qty, uom,
              inputUnitPrice, inputExtendedPrice, unitPrice, extendedPrice, usingInputPriceFallback, hasNoMatch, imageSrc,
            } = v;

            return (
              <Card key={key} className={`shadow-sm border-0 mb-3 ${hasNoMatch ? 'border border-danger' : ''}`}>
                <div className="mb-3">
                  <ExtractedFieldsBlock
                    partNumber={item?.normalized_parts?.part_number || ''}
                    manufacturer={item?.normalized_parts?.manufacturer || ''}
                    manufacturerPartNumber={item?.normalized_parts?.manufacturer_part_number || ''}
                    customerPartNumber={item?.normalized_parts?.customer_part_number || ''}
                    description={item?.normalized_parts?.description || ''}
                    notes={item?.normalized_parts?.notes || ''}
                    headerPrefix={(
                      <>
                        <Tag color="blue">LINE {lineNumberByKey[key]}</Tag>
                        {hasNoMatch && <Tag color="red">No match</Tag>}
                      </>
                    )}
                    headerExtra={(
                      <Button
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => setEditFieldsItemKey(key)}
                      >
                        Edit Details
                      </Button>
                    )}
                  />
              </div>

              <div className="d-flex gap-3 flex-wrap flex-md-nowrap">
                <ProductImage src={imageSrc} alt={selectedMatch?.match?.name || ''} />

                  <div className="flex-grow-1" style={{ minWidth: 200, ...(selectedMatch ? CARD_STYLES.matched : CARD_STYLES.unmatched) }}>
                    <div className="text-muted small fw-semibold mb-1" style={{ letterSpacing: 0.3 }}>
                      Matched Product
                    </div>
                  {selectedMatch ? (
                    <>
                      <div className="fw-semibold">
                        {selectedMatch.product_code}
                        {itemMatches.length > 1 ? ` (${itemMatches.length} options found)` : ''}
                      </div>
                      <div className="text-muted small">{decodeHtmlEntities(selectedMatch.match?.name || '')}</div>
                      {(() => {
                        const aiBadge = getMatchAiBadge(selectedMatch);
                        const uomMismatchBadge = getUomMismatchBadge(selectedMatch);
                        const erpBadge = getErpMatchBadge(selectedMatch);
                          const priceDivergenceBadge = getPriceDivergenceBadge(inputUnitPrice, unitPrice);
                        return (
                          <div className="text-muted small mt-1">
                              <MatchSourceBadge match={selectedMatch} className="me-1" />
                            {aiBadge && <Tag color={aiBadge.color} className="me-1">{aiBadge.label}</Tag>}
                            {uomMismatchBadge && (
                              <Tag color={uomMismatchBadge.color} className="me-1">{uomMismatchBadge.label}</Tag>
                            )}
                            {erpBadge && <Tag color={erpBadge.color} className="me-1">{erpBadge.label}</Tag>}
                              {priceDivergenceBadge && (
                                <Tag color={priceDivergenceBadge.color} className="me-1">{priceDivergenceBadge.label}</Tag>
                              )}
                            {selectedMatch.reasoning && (
                              <>
                                <span className="fw-semibold">Match reason: </span>
                                {decodeHtmlEntities(selectedMatch.reasoning)}
                              </>
                            )}
                          </div>
                        );
                      })()}
                    </>
                  ) : (
                    <div className="text-muted small">No catalog match found for this line.</div>
                  )}
                    <div className="d-flex gap-3 mt-1">
                  <Button
                    size="small"
                        danger={hasNoMatch}
                        icon={hasNoMatch ? <SearchOutlined /> : <SwapOutlined />}
                    onClick={() => setEditModalItemKey(key)}
                  >
                    {hasNoMatch
                      ? 'Search for a product'
                      : itemMatches.length > 1
                        ? `View ${itemMatches.length} match options`
                        : 'Edit match'}
                  </Button>
                    </div>
                </div>

                <PricingPanel
                  unitPrice={unitPrice}
                  uom={uom}
                  qty={qty}
                  onQtyChange={(val) => onQtyChange(key, val)}
                  extendedPrice={extendedPrice}
                  inputUnitPrice={inputUnitPrice}
                  inputExtendedPrice={inputExtendedPrice}
                  usingInputPriceFallback={usingInputPriceFallback}
                    priceLoading={Boolean(selectedCode && erpPriceLoadingByProductCode?.[selectedCode])}
                />
              </div>
            </Card>
          );
          })
        )}
            </div>
      </div>

      <EditMatchModal
        open={!!editModalItemKey}
        onClose={() => setEditModalItemKey(null)}
        matchType="product"
        item={editModalItem}
        itemMatches={editModalMatches}
        selectedCode={editModalSelectedCode}
        erpPriceByProductCode={erpPriceByProductCode}
        erpPriceLoadingByProductCode={erpPriceLoadingByProductCode}
        loadErpPricesForCodes={loadErpPricesForCodes}
        productImageBaseUrl={productImageBaseUrl}
        onSelectMatch={(productCode) => editModalItemKey && onSelectMatch(editModalItemKey, productCode)}
        onAddManualMatch={(product) => editModalItemKey && onAddManualMatch(editModalItemKey, product)}
      />

      <EditItemDetailsModal
        open={!!editFieldsItemKey}
        item={editFieldsItem}
        onCancel={() => setEditFieldsItemKey(null)}
        onSave={async (updatedFields) => {
          if (!onEditItemFields) {
            throw new Error('Save is unavailable right now — please reload and try again.');
          }
          await onEditItemFields(editFieldsItemKey, updatedFields);
          if (mountedRef.current) setEditFieldsItemKey(null);
        }}
      />
    </div>
  );
};

export default StepReviewMatch;
